import { Chip, TextField, TextFieldProps } from "@mui/material";
import React, { ChangeEvent } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

type HtmlEvent = ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

interface FormMultiInputChipsFieldProps<T extends FieldValues>
  extends Omit<TextFieldProps, "name"> {
  name: Path<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  label?: string;
  handleChange?: (e: HtmlEvent, fieldOnChange: (e: HtmlEvent) => void) => void;
  rules?: RegisterOptions<T>;
}

const FormMultiInputChipsField = <T extends FieldValues>({
  name,
  control,
  setValue,
  getValues,
  label,
  rules = {},
  ...rest
}: FormMultiInputChipsFieldProps<T>) => {
  const [inputValue, setInputValue] = React.useState("");

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setInputValue(event.target.value);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value }, fieldState: { error } }) => {
        return (
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter" && inputValue.trim()) {
                event.preventDefault();
                setInputValue("");
                setValue(name, [...value, inputValue.trim()] as any);
              } else if (
                event.key === "Backspace" &&
                !inputValue &&
                getValues(name)
              ) {
                const current = getValues(name);
                current.pop();
                setValue(name, current);
              }
            }}
            label={label}
            helperText={error ? error.message : null}
            fullWidth
            multiline
            variant="outlined"
            slotProps={{
              input: {
                startAdornment:
                  !Array.isArray(value) || value.length === 0 ? null : (
                    <div className="flex pr-3 gap-2 flex-wrap">
                      {value.map((v: any, index: number) => (
                        <Chip
                          key={v}
                          label={v}
                          variant="outlined"
                          disabled={rest.disabled}
                          onDelete={(): void => {
                            setValue(name, value.splice(index, 1) as any);
                          }}
                        />
                      ))}
                    </div>
                  ),
              },
            }}
            {...rest}
          />
        );
      }}
    />
  );
};

export default FormMultiInputChipsField;
